import { Card, CardContent } from "@/components/ui/card"
import type { ReconciledIncentive } from "@/hooks/useIncentives"

import { HELD, isRaised, pounds, splitOwed, upTo } from "./reconcileFormat"

// A route counts once even when it's checked under more than one incentive.
const summariseWeek = (incentives: ReconciledIncentive[]) => {
  const drivers = incentives.flatMap((incentive) => incentive.drivers)
  const routesToCheck = new Set(
    drivers.flatMap((driver) => [
      ...driver.no_block_route_codes,
      ...driver.ineligible_routes
        .filter((route) => route.reason === "not_closed")
        .map((route) => route.route_code),
    ]),
  )
  return {
    owed: splitOwed(incentives),
    driversOwed: new Set(
      drivers
        .filter((driver) => driver.amount_owed > 0 && !isRaised(driver))
        .map((driver) => driver.people_id),
    ).size,
    routesToCheck: routesToCheck.size,
  }
}

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div>
    <div className="text-xs text-palette-jungleGreen600">{label}</div>
    <div className="text-xl font-semibold tabular-nums text-palette-jungleGreen900">{value}</div>
    {hint && <div className="text-xs text-palette-jungleGreen600">{hint}</div>}
  </div>
)

export default function ReconcileSummary({ incentives }: { incentives: ReconciledIncentive[] }) {
  const { owed, driversOwed, routesToCheck } = summariseWeek(incentives)
  return (
    <Card role="region" aria-label="Week totals">
      <CardContent className="flex flex-wrap gap-x-12 gap-y-4 p-4">
        <Stat label="Ready to raise" value={pounds.format(owed.ready)} />
        <Stat
          label={HELD}
          value={upTo(owed.held)}
          hint="Raised once Ops approve the routes in Moneta"
        />
        <Stat
          label="Raised"
          value={pounds.format(owed.raised)}
          hint="Tickets already raised for the week"
        />
        <Stat label="Drivers owed" value={String(driversOwed)} />
        <Stat
          label="Routes to check"
          value={String(routesToCheck)}
          hint="No block that day, or not closed"
        />
      </CardContent>
    </Card>
  )
}
